import { useCallback } from 'react';

/* eslint-disable import/order -- CI lints `-api` before `/` (correct ASCII), local eslint --fix reverses it; disable to keep CI happy. */
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
/* eslint-enable import/order */

import { useSpaceStoreActions } from '../../controllers/space-store/hooks';

import { deleteSpaceSourceFetchAction } from './useDeleteSpaceSource.fetch';

type UseDeleteSpaceSourceProps = {
	spaceId: string;
	cloudId: string;
	product?: string;
	experienceId?: string;
};

type UseDeleteSpaceSourceResult = {
	deleteSource: (sourceId: string) => Promise<void>;
	isDeleting: boolean;
	error: ApiError | null;
};

/**
 * Removes a source from a space. Wraps the shared `useMutation` so the
 * in-flight DELETE is aborted on unmount/supersede and `isDeleting` /
 * `error` come from the canonical reducer. On success the deletion is
 * mirrored into the spaces sweet-state store so any open
 * ManageSpaceModal / side-nav row reflects the removal immediately.
 *
 * Public API (`deleteSource(sourceId)`, `isDeleting`, `error`) is
 * unchanged from the previous manual implementation.
 */
export const useDeleteSpaceSource = ({
	spaceId,
	cloudId,
	product = 'rovo',
	experienceId = 'ai-mate',
}: UseDeleteSpaceSourceProps): UseDeleteSpaceSourceResult => {
	const { removeSpaceSource } = useSpaceStoreActions();

	const { mutate, isSubmitting, error } = useMutation<string, void>({
		mutationFn: async (sourceId) => {
			await deleteSpaceSourceFetchAction({
				spaceId,
				sourceId,
				cloudId,
				product,
				experienceId,
			});
			// Mirror the deletion. Done here (not in top-level `onSuccess`)
			// because that callback receives only `data` — we'd lose `sourceId`.
			removeSpaceSource(spaceId, sourceId);
		},
	});

	// Re-throw on failure to preserve the previous `Promise<void>` contract
	// — callers (e.g. ChatView) rely on it for optimistic-UI rollback.
	const deleteSource = useCallback(
		async (sourceId: string) => {
			let caught: Error | null = null;
			await mutate(sourceId, {
				onError: (err) => {
					caught = err;
				},
			});
			if (caught !== null) {
				// `caught` is a real `Error`/`ApiError` instance from the mutation
				// reducer; oxlint's `no-throw-literal` is purely syntactic and
				// can't see the captured-from-callback type, so disable here.
				// eslint-disable-next-line no-throw-literal
				throw caught;
			}
		},
		[mutate],
	);

	return { deleteSource, isDeleting: isSubmitting, error };
};
